import { motion } from 'framer-motion';

type Service = {
  tag: string;
  title: string;
  bullets: string[];
  cta: string;
  href: string;
  bg: string;
  text: string;
  accent: string;
  icon: string;
};

const services: Service[] = [
  {
    tag: '01 — Hyr',
    title: 'Uthyrning',
    bullets: [
      'Hyr hoppborgar, hinderbanor och aktiviteter för en eller flera dagar',
      'Levererar till Jönköping, Tenhult, Nässjö och hela Småland',
      'Snabb support och säker installation',
    ],
    cta: 'Boka uthyrning',
    href: '#kontakt',
    bg: 'bg-sun',
    text: 'text-ink',
    accent: 'bg-tomato',
    icon: '🎈',
  },
  {
    tag: '02 — Köp',
    title: 'Försäljning',
    bullets: [
      'Över 600 modeller att välja mellan',
      'Designa din egen hoppborg med logo',
      'Direktförsäljning och tillverkning i Europa',
    ],
    cta: 'Se modeller',
    href: '/aktiviteter',
    bg: 'bg-sky',
    text: 'text-cream',
    accent: 'bg-sun',
    icon: '🏰',
  },
  {
    tag: '03 — Event',
    title: 'Evenemang',
    bullets: [
      'Vi levererar aktiviteter till event och turnéer',
      'Erfarenhet från Diggiloo-turnén',
      'Helhetslösningar för hela arrangemanget',
    ],
    cta: 'Planera event',
    href: '#kontakt',
    bg: 'bg-grass',
    text: 'text-ink',
    accent: 'bg-grape',
    icon: '🎪',
  },
];

export default function Services() {
  return (
    <section id="tjanster" className="relative py-16 sm:py-20 bg-sand">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Vad vi gör"
          title={
            <>
              Tre sätt att <span className="text-tomato">studsa</span> in glädjen
              i din nästa tillställning.
            </>
          }
          description="Från spontana barnkalas i Jönköping till nationella turnéer — vi levererar färdiga upplevelser med trygg leverans, installation och support i hela Småland."
        />

        <div className="mt-14 grid md:grid-cols-3 gap-5 lg:gap-7">
          {services.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -8 }}
              className={`group relative rounded-[2rem] ${s.bg} ${s.text} p-7 sm:p-8 shadow-pop overflow-hidden`}
            >
              {/* Decorative blob */}
              <div className={`absolute -top-12 -right-10 size-44 rounded-full ${s.accent} opacity-30 blur-2xl transition-opacity group-hover:opacity-50`} />

              <div className="relative flex items-start justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider opacity-70">{s.tag}</span>
                <motion.span
                  className="text-4xl"
                  whileHover={{ rotate: [-8, 8, -8, 0], scale: 1.15 }}
                  transition={{ duration: 0.5 }}
                  aria-hidden
                >
                  {s.icon}
                </motion.span>
              </div>

              <h3 className="relative mt-8 font-display text-3xl sm:text-4xl font-semibold leading-none">
                {s.title}
              </h3>

              <ul className="relative mt-6 space-y-3 text-[15px] leading-relaxed">
                {s.bullets.map((b) => (
                  <li key={b} className="flex gap-3">
                    <span className={`mt-1.5 size-2 shrink-0 rounded-full ${s.accent}`} />
                    <span className="opacity-90">{b}</span>
                  </li>
                ))}
              </ul>

              <a
                href={s.href}
                className="relative mt-8 inline-flex items-center gap-2 font-semibold text-[15px] underline-offset-4 hover:underline"
              >
                {s.cta}
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  align = 'left',
}: {
  eyebrow: string;
  title: React.ReactNode;
  description?: string;
  align?: 'left' | 'center';
}) {
  const isCenter = align === 'center';
  return (
    <div className={`max-w-3xl ${isCenter ? 'mx-auto text-center' : ''}`}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className={`inline-flex items-center gap-2 rounded-full bg-ink/5 border border-ink/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-ink/70`}
      >
        <span className="size-1.5 rounded-full bg-tomato" />
        {eyebrow}
      </motion.div>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.05 }}
        className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02] text-balance"
      >
        {title}
      </motion.h2>
      {description && (
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-5 text-lg text-ink/65 max-w-2xl text-pretty"
        >
          {description}
        </motion.p>
      )}
    </div>
  );
}
